import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {PortfolioType} from "../../../portfolio/models/Portfolio";
import {EquityPositionViewModel, EquityPositionViewModels} from "../models/EquityPositionViewModel";
import {QuoteStore} from "../../../quote/store/QuoteStore";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {QuoteViewModel} from "../../../quote/models/QuoteViewModel";
import {QuoteRequests} from "../../../quote/models/QuoteRequest";
import {EquityAccountId, EquityAccountRecord} from "../types/EquityAccountRecord";
import {equityPositionMatcher, EquityPositionRecords} from "../types/EquityPositionRecord";
import {EquityAccountService} from "../services/EquityAccountService";
import {SecurityInformationStore} from "../../../security/info/store/SecurityInformationStore";
import {SecurityInformationRecord} from "../../../security/info/types/SecurityInformationRecord";
import {
    TechnicalIndicatorsRecord,
    TechnicalIndicatorsRecords
} from "../../../technical-indicators/types/TechnicalIndicatorsRecord";
import {SecurityMap, SecurityMapOf} from "../../../security/common/map/SecurityMap";
import {DividendsStore} from "../../../dividends/store/DividendsStore";
import {TechnicalIndicatorsStore} from "../../../technical-indicators/store/TechnicalIndicatorsStore";
import {DividendsModel} from "../../../dividends/models/DividendsModel";
import {PriceStrategyService} from "../../../components/inputs/price/service/PriceStrategyService";
import {ClientEventService} from "../../../services/client-events/ClientEventService";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import {AveragePriceUpdateRequestService} from "../../services/AveragePriceUpdateRequestService";
import {
    PendingAveragePriceUpdateRecord,
    PendingAveragePriceUpdateRecords
} from "../../types/PendingAveragePriceUpdateRecord";
import {isPortfolioClientEventFor} from "../../../services/client-events/models/ClientEventRecord";

export class EquityAccountViewStore {

    private readonly account$ = new ReplaySubject<EquityAccountRecord>(1);
    private readonly positions$: Observable<EquityPositionRecords>;
    private readonly models$: Observable<EquityPositionViewModels>;

    private readonly reloadAveragePriceUpdates$ = new BehaviorSubject(true);

    public constructor(private readonly equityAccountService: EquityAccountService,
                       private readonly quoteStore: QuoteStore,
                       private readonly securityInformationStore: SecurityInformationStore,
                       private readonly dividendsStore: DividendsStore,
                       private readonly technicalIndicatorsStore: TechnicalIndicatorsStore,
                       private readonly priceStrategyService: PriceStrategyService,
                       private readonly clientEventService: ClientEventService,
                       private readonly averagePriceUpdateRequestService: AveragePriceUpdateRequestService) {
        this.positions$ = this.buildPositionsStream();
        this.models$ = this.buildModelStream();
    }

    private buildPositionsStream(): Observable<EquityPositionRecords> {
        const updateSource$ = Observable.combineLatest(this.account$, this.clientEventService.events());
        const updated$ = updateSource$.filter(([account, evt]) => (account != null) && isPortfolioClientEventFor(evt, account.id))
                                      .map(() => true)
                                      .startWith(true);
        const source$ = Observable.combineLatest(this.account$, updated$);
        return source$.map(([account]) => account)
                      .map((acc) => acc.id)
                      .switchMap((id) => this.retrievePositions(id))
                      .publishReplay(1)
                      .refCount();
    }

    private retrievePositions(id: EquityAccountId): Observable<EquityPositionRecords> {
        return Observable.of(id)
                         .flatMap((i) => this.equityAccountService.positions(i))
                         .catch((err) => Observable.empty());
    }

    private retrievePendingAveragePriceUpdates(id: EquityAccountId): Observable<PendingAveragePriceUpdateRecords> {
        return Observable.of(id)
                         .flatMap((i) => this.averagePriceUpdateRequestService.getPendingAveragePriceUpdates(i))
                         .catch((err) => Observable.empty());
    }

    private buildModelStream(): Observable<EquityPositionViewModels> {
        const distinctPositions$ = this.positions$
                                       .distinctUntilChanged((current, previous) => this.positionsUnchanged(current,
                                                                                                            previous))
                                       .publishReplay(1)
                                       .refCount();
        const accountType$ = this.account$.map((a) => a.accountType);
        const accountId$ = this.account$.map((a) => a.id);

        const initialQuotes = SecurityMapOf<QuoteViewModel>();
        const quotes$ = Observable.combineLatest(distinctPositions$, accountType$)
                                  .map(([pos, portfolioType]) => this.quoteRequests(pos, portfolioType))
                                  .switchMap((requests) => this.quoteStore.stream(requests))
                                  .scan((acc, next) => acc.set(next, next), initialQuotes)
                                  .startWith(initialQuotes)
                                  .auditTime(100);

        const initialSecurityInfo = SecurityMapOf<SecurityInformationRecord>();

        const securityInfo$ = distinctPositions$.switchMap((positions) => this.securityInformationStore.stream(positions))
                                                .scan((acc, next) => acc.set(next, next), initialSecurityInfo)
                                                .startWith(SecurityMapOf<SecurityInformationRecord>())
                                                .auditTime(100);

        const dividends$ = distinctPositions$.switchMap((positions) => this.dividendsStream(positions));
        const indicators$ = distinctPositions$.switchMap((positions) => this.indicatorsStream(positions));

        const averagePriceUpdatesSource$ = Observable.combineLatest(accountId$, this.reloadAveragePriceUpdates$);

        const pendingAveragePriceUpdates$ = averagePriceUpdatesSource$.map(([accountId]) => accountId)
                                                                      .switchMap((id) => this.retrievePendingAveragePriceUpdates(id))
                                                                      .map((pendingChanges) => {
                                                                          const changeMap = SecurityMapOf<PendingAveragePriceUpdateRecord>();
                                                                          pendingChanges.forEach((change) => changeMap.set(change, change));
                                                                          return changeMap;
                                                                      }).startWith(SecurityMapOf<PendingAveragePriceUpdateRecord>());

        const source$ = Observable.combineLatest(accountId$, pendingAveragePriceUpdates$, distinctPositions$, quotes$, securityInfo$, dividends$, indicators$, accountType$);

        return source$.auditTime(100)
                      .map(([accountId, pendingUpdates, positions, quotes, securityInfo, dividends, indicators, portfolioType]) => {
                          return this.buildModels(accountId, pendingUpdates,  positions, quotes, securityInfo, dividends, indicators, portfolioType);
                      })
                      .publishReplay(1)
                      .refCount();
    }

    private positionsUnchanged(current: EquityPositionRecords,
                               previous: EquityPositionRecords): boolean {
        return ArrayUtil.matches(current, previous, equityPositionMatcher());
    }

    private quoteRequests(positions: EquityPositionRecords, portfolioType: PortfolioType): QuoteRequests {
        return positions.map((pos) => {
            return {
                code: pos.code,
                exchange: pos.exchange,
                portfolioType
            };
        });
    }

    private dividendsStream(positions: EquityPositionRecords): Observable<SecurityMap<DividendsModel>> {
        return this.dividendsStore
                   .stream(positions)
                   .scan((acc, next) => acc.set(next, next), SecurityMapOf<DividendsModel>())
                   .auditTime(100)
                   .startWith(SecurityMapOf<DividendsModel>());
    }

    private indicatorsStream(positions: EquityPositionRecords): Observable<TechnicalIndicatorsRecords> {
        return this.technicalIndicatorsStore.stream(positions)
                   .scan((acc, next) => {
                       acc.set(next.code, next);
                       return acc;
                   }, new Map<string, TechnicalIndicatorsRecord>())
                   .auditTime(100)
                   .map((cache) => cache.values())
                   .map((values) => Array.from(values))
                   .startWith([]);
    }

    private buildModels(accountId: EquityAccountId,
                        pendingUpdates: SecurityMap<PendingAveragePriceUpdateRecord>,
                        positions: EquityPositionRecords,
                        quotes: SecurityMap<QuoteViewModel>,
                        securityInfo: SecurityMap<SecurityInformationRecord>,
                        dividends: SecurityMap<DividendsModel>,
                        indicators: TechnicalIndicatorsRecords,
                        portfolioType: PortfolioType): EquityPositionViewModels {
        const asAt = DateTimeUtil.isoToday();

        return positions.map((pos) => {
            const quote = quotes.get(pos);
            const info = securityInfo.get(pos);
            const dividend = dividends.get(pos);
            const techIndicators = indicators.find((i) => i.code === pos.code)!;
            const strategy = this.priceStrategyService.forCode(pos.code);
            const pendingUpdate = pendingUpdates.get(pos);
            return EquityPositionViewModel.create(accountId, asAt, pos, quote, info, dividend, techIndicators, pendingUpdate, strategy, portfolioType);
        });
    }

    public updateAccount(account: EquityAccountRecord) {
        this.account$.next(account);
    }

    public reloadAveragePriceUpdates() {
        this.reloadAveragePriceUpdates$.next(true);
    }

    public get account(): Observable<EquityAccountRecord> {
        return this.account$.observeOn(Scheduler.async);
    }

    public get positions(): Observable<EquityPositionViewModels> {
        return this.models$.observeOn(Scheduler.async);
    }
}
